import {
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Stack,
    Tag,
    TagLabel,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AssetClass } from '../_openapi/portfolio-optimizer';
import { useAssetLookup } from '../api/asset';
import { usePortfolioLookup } from '../api/portfolio';
import { useAssetPrice } from '../api/pricing';
import { AssetClassTag } from '../widgets/AssetClassTag';
import { SegmentedControls } from '../widgets/SegmentedControls';
import { MoneyCell } from '../widgets/Table/MoneyCell';
import { NumberCell } from '../widgets/Table/NumberCell';
import { ManualTrade } from './TradeBlotter';
import { useTradeBlotterContext } from './TradeBlotterContext';
import { tradeCalculator } from './tradeCalculator';

export const TradeBudgetModal = ({
    isOpen,
    onClose,
    onAdd,
    trade,
}: {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (data: ManualTrade) => void;
    trade: ManualTrade;
}) => {
    const [budget, setBudget] = useState<number>(0);
    const [side, setSide] = useState<'BUY' | 'SELL' | undefined>();

    const { data: assetLookup, isLoading: assetLoading } = useAssetLookup(trade.asset?.sId);
    const { data: portfolioLookup, isLoading: portfolioLoading } = usePortfolioLookup(trade.portfolio?.pId);

    const asset = assetLookup?.[0];
    const portfolio = portfolioLookup?.[0];
    const { data: quote, isFetching } = useAssetPrice(trade.asset?.sId);

    const manualTrade = useMemo<ManualTrade>(() => {
        const data: ManualTrade = {
            asset,
            portfolio: portfolio?.base,
            orderType: 'LMT',
            side,
            price: {
                type: tradeCalculator.getPriceTypeFromTradeSide(side),
            },
            budget,
            quote,
        };
        data.quantity = tradeCalculator.getTradeQuantity(data);
        return data;
    }, [asset, budget, portfolio, quote, side]);

    const submit = useCallback(() => {
        onAdd(manualTrade);
        onClose();
    }, [manualTrade, onAdd, onClose]);

    useEffect(() => {
        if (!isOpen) {
            setBudget(0);
            setSide(undefined);
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Calculate trade</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack>
                        <Flex gap={4} alignItems="center">
                            <Text width="120px">Security</Text>
                            <Flex gap={1}>
                                <AssetClassTag size="md" assetClass={asset?.assetClass as AssetClass} />
                                <Tag size="md" flexShrink={0}>
                                    <TagLabel>{asset?.symbol}</TagLabel>
                                </Tag>
                                <Text>{asset?.description}</Text>
                            </Flex>
                        </Flex>
                        {trade.portfolio && (
                            <Flex gap={4} alignItems="center">
                                <Text width="120px">Portfolio</Text>
                                <Flex gap={1}>
                                    {portfolio?.base.accountAlias && (
                                        <Tag size="md" flexShrink={0}>
                                            <TagLabel>{portfolio?.base.accountAlias}</TagLabel>
                                        </Tag>
                                    )}
                                    <Tag size="md" flexShrink={0}>
                                        <TagLabel>{portfolio?.base.portfolioGuid}</TagLabel>
                                    </Tag>
                                </Flex>
                            </Flex>
                        )}
                        <Flex gap={4} alignItems="center">
                            <Text width="120px">Budget</Text>
                            <NumberInput
                                width="200px"
                                size="sm"
                                value={budget}
                                onChange={(_, value) => setBudget(value || 0)}
                            >
                                <NumberInputField />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                        </Flex>
                        <Flex gap={4} alignItems="center">
                            <Text width="120px">Side</Text>
                            <SegmentedControls
                                onClick={setSide}
                                selectedValue={side}
                                options={[
                                    { value: 'BUY', label: 'Buy' },
                                    { value: 'SELL', label: 'Sell' },
                                ]}
                                size="xs"
                            />
                        </Flex>
                        <Flex gap={4} alignItems="center">
                            <Text width="120px">Clean Price</Text>
                            <MoneyCell value={tradeCalculator.getAdjustedCleanPrice(manualTrade)} fractionDigits={4} />
                        </Flex>
                        {asset?.assetClass === 'Bond' && (
                            <>
                                <Flex gap={4} alignItems="center">
                                    <Text width="120px">Accrued Interest</Text>
                                    <MoneyCell
                                        value={tradeCalculator.getAccruedInterest(manualTrade)}
                                        fractionDigits={4}
                                    />
                                </Flex>
                                <Flex gap={4} alignItems="center">
                                    <Text width="120px">Dirty Price</Text>
                                    <MoneyCell
                                        value={tradeCalculator.getAdjustedDirtyPrice(manualTrade)}
                                        fractionDigits={4}
                                    />
                                </Flex>
                            </>
                        )}

                        <Flex gap={4} alignItems="center">
                            <Text width="120px">Quantity</Text>
                            <NumberCell value={manualTrade.quantity} />
                        </Flex>
                        <Flex gap={4} alignItems="center">
                            <Text width="120px">Trade Value</Text>
                            <MoneyCell value={tradeCalculator.getTradeValue(manualTrade)} />
                        </Flex>
                    </Stack>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="teal"
                        onClick={submit}
                        isDisabled={!budget || !side || !quote || assetLoading || portfolioLoading || isFetching}
                    >
                        Add
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export const useTradeBudgetModalControls = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { addTrade } = useTradeBlotterContext();

    const [trade, setTrade] = useState<ManualTrade>({});

    const calculateTrade = useCallback(
        (manualTrade: ManualTrade) => {
            setTrade(manualTrade);
            onOpen();
        },
        [onOpen],
    );

    return {
        onAdd: addTrade,
        isOpen,
        onClose,
        calculateTrade,
        trade,
    };
};
